import React from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Tab, Tabs } from 'react-bootstrap';

import ConfirmationModal from '../../Components/ConfirmationModal/ConfirmationModal';
import ServerTable from '../../Components/Table/ServerTabe';
import { ConcurExpenseProcessingApi } from '../../Services/ConciliationApiDataService';
import { formatGuatemalanDateTime } from '../../Utils/dateFormat';
import { formatCurrencyNum, formatDate } from '../../Utils/formaters';

const MAX_REPORTS_PER_REQUEST = 100;

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.Message || error?.message || fallback;

const isSuccessfulReport = (report) => report?.success === true && report?.status === 'Succeeded';

const isSubmittedReport = (report) =>
  (isSuccessfulReport(report) && report?.submitted === true) ||
  report?.alreadySubmitted === true ||
  report?.status === 'AlreadySubmitted';

const statusVariant = (report) => {
  if (isSuccessfulReport(report)) return 'success';
  if (report?.status === 'AlreadySubmitted') return 'secondary';
  if (report?.status === 'NotEligible' || report?.status === 'Invalid') return 'warning';
  return 'danger';
};

const previousMonth = () => {
  const date = new Date();
  date.setDate(1);
  date.setMonth(date.getMonth() - 1);
  return { year: date.getFullYear(), month: date.getMonth() + 1 };
};

const SelectionCheckbox = ({ checked, indeterminate = false, ...props }) => {
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <input
      {...props}
      ref={inputRef}
      type="checkbox"
      className="form-check-input"
      checked={checked}
    />
  );
};

const ExpenseResult = ({ expense }) => (
  <div className={`concur-expense-result ${expense.success ? '' : 'concur-expense-result-failed'}`}>
    <div className="d-flex flex-wrap align-items-center gap-2">
      <span className="fw-semibold">{expense.expenseId || expense.conciliationKey || 'Expense'}</span>
      <Badge bg={expense.success ? 'success' : 'danger'}>{expense.status || (expense.success ? 'Succeeded' : 'Failed')}</Badge>
      {expense.usedDummyPdf && <Badge bg="warning" text="dark">Dummy PDF used</Badge>}
      {expense.isCreditCard && <Badge bg="secondary">Credit card</Badge>}
    </div>
    <div className="small text-muted mt-1">
      Location {expense.locationUpdated ? 'updated' : 'not updated'} · Image {expense.imageUploaded ? 'uploaded' : 'not uploaded'}
    </div>
    {(expense.messages || []).length > 0 && (
      <ul className="small mb-0 mt-2 ps-3">
        {expense.messages.map((message, index) => <li key={`${expense.expenseId}-${index}`}>{message}</li>)}
      </ul>
    )}
  </div>
);

export const ConcurProcessingResults = ({ result }) => {
  if (!result) return null;

  const reports = result.reports || [];
  const succeededReports = reports.filter(isSuccessfulReport).length;
  const failedReports = reports.length - succeededReports;

  return (
    <section className="concur-results" aria-live="polite" aria-label="Concur processing results">
      <h6 className="fw-bold mb-2">Processing results</h6>
      <div className="summary-grid mb-3">
        <div className="summary-stat"><span className="label">Reports</span><span className="value">{reports.length}</span></div>
        <div className="summary-stat"><span className="label">Succeeded</span><span className="value text-success">{succeededReports}</span></div>
        <div className="summary-stat"><span className="label">Failed / not processed</span><span className="value text-danger">{failedReports}</span></div>
      </div>

      <div className="d-flex flex-column gap-2">
        {reports.map((report) => {
          const expenses = report.expenses || [];
          const failed = !isSuccessfulReport(report);
          return (
            <Card key={report.reportId} className={`concur-report-result ${failed ? 'border-danger' : 'border-success'}`}>
              <Card.Body>
                <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                  <div>
                    <div className="fw-bold">{report.reportName || report.reportId}</div>
                    <div className="small text-muted">{report.reportId}</div>
                  </div>
                  <Badge bg={statusVariant(report)}>{report.status || (report.success ? 'Succeeded' : 'Failed')}</Badge>
                </div>
                {report.message && <p className="small mb-2 mt-2">{report.message}</p>}
                <div className="small text-muted">
                  {report.succeededExpenses || 0} of {report.totalExpenses || 0} expenses succeeded
                </div>
                {expenses.length > 0 && (
                  <details className="mt-2" open={failed ? undefined : false}>
                    <summary className="concur-expense-summary">
                      {failed ? 'View expense-level failures and details' : 'View expense details'}
                    </summary>
                    <div className="d-flex flex-column gap-2 mt-2">
                      {expenses.map((expense, index) => (
                        <ExpenseResult key={expense.expenseId || expense.conciliationKey || index} expense={expense} />
                      ))}
                    </div>
                  </details>
                )}
              </Card.Body>
            </Card>
          );
        })}
      </div>
    </section>
  );
};

const MonthlyBulkPanel = ({ busy, onRequestQueue, queuedJob }) => {
  const defaults = React.useMemo(previousMonth, []);
  const [monthMode, setMonthMode] = React.useState('previous');
  const [year, setYear] = React.useState(String(defaults.year));
  const [month, setMonth] = React.useState(String(defaults.month));

  const numericYear = Number(year);
  const numericMonth = Number(month);
  const validSpecificMonth =
    Number.isInteger(numericYear) && numericYear >= 2000 && numericYear <= 9999 &&
    Number.isInteger(numericMonth) && numericMonth >= 1 && numericMonth <= 12;

  return (
    <div className="extraction-panel concur-monthly-panel">
      <div className="panel-row">
        <h6 className="fw-bold">Monthly bulk processing</h6>
        <p className="text-muted small mb-3">
          Queue the automated Concur credit-card workflow. The request only queues a background job; it does not wait for processing to finish.
        </p>
        <Form.Check
          type="radio"
          id="concur-previous-month"
          name="concur-month-mode"
          label="Previous month"
          checked={monthMode === 'previous'}
          disabled={busy}
          onChange={() => setMonthMode('previous')}
        />
        <Form.Check
          className="mt-2"
          type="radio"
          id="concur-specific-month"
          name="concur-month-mode"
          label="Specific month"
          checked={monthMode === 'specific'}
          disabled={busy}
          onChange={() => setMonthMode('specific')}
        />

        {monthMode === 'specific' && (
          <Row className="g-2 mt-1">
            <Col sm={6} md={4}>
              <Form.Label htmlFor="concur-year">Year</Form.Label>
              <Form.Control
                id="concur-year"
                type="number"
                min={2000}
                max={9999}
                step={1}
                value={year}
                isInvalid={year !== '' && !(Number.isInteger(numericYear) && numericYear >= 2000 && numericYear <= 9999)}
                disabled={busy}
                onChange={(event) => setYear(event.target.value)}
              />
              <Form.Control.Feedback type="invalid">Enter a year from 2000 through 9999.</Form.Control.Feedback>
            </Col>
            <Col sm={6} md={4}>
              <Form.Label htmlFor="concur-month">Month</Form.Label>
              <Form.Control
                id="concur-month"
                type="number"
                min={1}
                max={12}
                step={1}
                value={month}
                isInvalid={month !== '' && !(Number.isInteger(numericMonth) && numericMonth >= 1 && numericMonth <= 12)}
                disabled={busy}
                onChange={(event) => setMonth(event.target.value)}
              />
              <Form.Control.Feedback type="invalid">Enter a month from 1 through 12.</Form.Control.Feedback>
            </Col>
          </Row>
        )}
      </div>

      <Button
        variant="outline-primary"
        disabled={busy || (monthMode === 'specific' && !validSpecificMonth)}
        onClick={() => onRequestQueue(monthMode === 'specific' ? { year: numericYear, month: numericMonth } : {})}
      >
        {busy && <Spinner animation="border" size="sm" className="me-2" />}
        {busy ? 'Queueing workflow...' : 'Queue monthly processing'}
      </Button>

      {queuedJob && (
        <Alert variant="success" className="mt-3 mb-0" role="status">
          Monthly processing was queued with job ID <strong>{queuedJob}</strong>. Processing is not yet complete.
        </Alert>
      )}
    </div>
  );
};

const normalizeSingleResult = (report) => ({
  totalReports: 1,
  succeededReports: isSuccessfulReport(report) ? 1 : 0,
  failedReports: isSuccessfulReport(report) ? 0 : 1,
  reports: [report]
});

export const ConcurExpenseProcessingPanel = ({ companies = [], acquireToken, showInfoModal }) => {
  const [mode, setMode] = React.useState('selected');
  const [companyId, setCompanyId] = React.useState('');
  const [selectedIds, setSelectedIds] = React.useState(() => new Set());
  const [pageItems, setPageItems] = React.useState([]);
  const [refreshTick, setRefreshTick] = React.useState(0);
  const [processing, setProcessing] = React.useState(null);
  const [confirmation, setConfirmation] = React.useState(null);
  const [result, setResult] = React.useState(null);
  const [loadError, setLoadError] = React.useState('');
  const [queuedJob, setQueuedJob] = React.useState('');

  const companyNames = React.useMemo(
    () => new Map(companies.map((company) => [Number(company.companyId), company.companyName || `Company ${company.companyId}`])),
    [companies]
  );

  const fetchReports = React.useCallback(async ({ pageIndex, pageSize, globalFilter }) => {
    try {
      setLoadError('');
      const token = await acquireToken();
      const response = await ConcurExpenseProcessingApi.getReports({
        companyId: companyId === '' ? undefined : Number(companyId),
        search: globalFilter?.trim() || undefined,
        page: pageIndex + 1,
        pageSize
      }, token);
      const items = response?.items || [];
      setPageItems(items);
      return { items, totalItems: response?.totalCount || 0 };
    } catch (error) {
      setPageItems([]);
      setLoadError(getErrorMessage(error, 'Could not load synchronized Concur reports.'));
      throw error;
    }
  }, [acquireToken, companyId]);

  const updateSelection = React.useCallback((reportId, selected) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (selected) {
        if (next.size >= MAX_REPORTS_PER_REQUEST) return current;
        next.add(reportId);
      } else {
        next.delete(reportId);
      }
      return next;
    });
  }, []);

  const selectablePageItems = React.useMemo(() => pageItems.filter((report) => report.canProcess), [pageItems]);
  const selectedOnPage = selectablePageItems.filter((report) => selectedIds.has(report.reportId)).length;
  const allPageSelected = selectablePageItems.length > 0 && selectedOnPage === selectablePageItems.length;

  const toggleCurrentPage = React.useCallback((selected) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (!selected) {
        selectablePageItems.forEach((report) => next.delete(report.reportId));
        return next;
      }
      for (const report of selectablePageItems) {
        if (next.size >= MAX_REPORTS_PER_REQUEST) break;
        next.add(report.reportId);
      }
      return next;
    });
  }, [selectablePageItems]);

  const requestSingleProcessing = React.useCallback((report) => {
    setConfirmation({ type: 'single', reportId: report.reportId, reportName: report.name || report.reportId });
  }, []);

  const columns = React.useMemo(() => [
    {
      id: 'selection',
      header: () => (
        <SelectionCheckbox
          checked={allPageSelected}
          indeterminate={selectedOnPage > 0 && !allPageSelected}
          disabled={processing !== null || selectablePageItems.length === 0}
          onChange={(event) => toggleCurrentPage(event.target.checked)}
          aria-label="Select all processable reports on this page"
        />
      ),
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }) => {
        const report = row.original;
        const selectionLimitReached = selectedIds.size >= MAX_REPORTS_PER_REQUEST && !selectedIds.has(report.reportId);
        return (
          <SelectionCheckbox
            checked={selectedIds.has(report.reportId)}
            disabled={!report.canProcess || processing !== null || selectionLimitReached}
            title={!report.canProcess ? (report.blockingReason || 'This report cannot be processed.') : undefined}
            onChange={(event) => updateSelection(report.reportId, event.target.checked)}
            aria-label={`Select report ${report.reportId}`}
          />
        );
      }
    },
    {
      id: 'report',
      header: 'Report',
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }) => (
        <div className="concur-report-cell">
          <div className="fw-semibold">{row.original.name || 'Unnamed report'}</div>
          <div className="small text-muted text-break">{row.original.reportId}</div>
          {!row.original.canProcess && (
            <div className="small text-danger mt-1" role="note">{row.original.blockingReason || 'This report cannot be processed.'}</div>
          )}
        </div>
      )
    },
    {
      id: 'owner',
      header: 'Owner',
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }) => (
        <div>
          <div>{row.original.ownerDisplayName || '-'}</div>
          <div className="small text-muted">{row.original.ownerUserName || '-'}</div>
        </div>
      )
    },
    // {
    //   id: 'company',
    //   header: 'Company',
    //   enableSorting: false,
    //   enableColumnFilter: false,
    //   cell: ({ row }) => companyNames.get(Number(row.original.companyId)) || `Company ${row.original.companyId}`
    // },
    {
      id: 'total',
      header: 'Total',
      headerStyle: 'text-end',
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }) => <div className="text-nowrap text-end">{formatCurrencyNum(row.original.total)} {row.original.currencyCode || ''}</div>
    },
    {
      id: 'expenses',
      header: 'Expenses',
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }) => (
        <div>
          <div className='text-center'>{row.original.expenseCount ?? 0}</div>
          <div className="small text-muted text-nowrap text-center">
            {row.original.firstExpenseDate || row.original.lastExpenseDate
              ? `${formatDate(row.original.firstExpenseDate) || '-'} – ${formatDate(row.original.lastExpenseDate) || '-'}`
              : 'No expense dates'}
          </div>
        </div>
      )
    },
    {
      id: 'lastModified',
      header: 'Last modified',
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }) => row.original.lastModifiedDate ? formatGuatemalanDateTime(row.original.lastModifiedDate) : '-'
    },
    {
      id: 'action',
      header: '',
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }) => (
        <Button
          size="sm"
          variant="outline-primary"
          className="text-nowrap"
          disabled={!row.original.canProcess || processing !== null}
          onClick={() => requestSingleProcessing(row.original)}
          aria-label={`Process report ${row.original.reportId}`}
        >
          Process
        </Button>
      )
    }
  ], [
    allPageSelected,
    companyNames,
    processing,
    requestSingleProcessing,
    selectablePageItems.length,
    selectedIds,
    selectedOnPage,
    toggleCurrentPage,
    updateSelection
  ]);

  const applyResults = React.useCallback((response) => {
    setResult(response);
    const submittedIds = new Set((response.reports || []).filter(isSubmittedReport).map((report) => report.reportId));
    setSelectedIds((current) => new Set([...current].filter((reportId) => !submittedIds.has(reportId))));
    setRefreshTick((tick) => tick + 1);
  }, []);

  const executeConfirmed = async () => {
    if (!confirmation || processing !== null) return;
    const request = confirmation;
    setConfirmation(null);

    try {
      if (request.type === 'monthly') {
        setProcessing('monthly');
        const token = await acquireToken();
        const response = await ConcurExpenseProcessingApi.queueMonthly(request.params, token);
        setQueuedJob(response?.jobId || 'Unknown');
        showInfoModal?.(
          'Monthly processing queued',
          <p className="mb-0">The workflow was queued with job ID <strong>{response?.jobId || 'Unknown'}</strong>. Processing is not yet complete.</p>
        );
        return;
      }

      setProcessing(request.type);
      const token = await acquireToken();
      if (request.type === 'single') {
        const response = await ConcurExpenseProcessingApi.processReport(request.reportId, token);
        applyResults(normalizeSingleResult(response));
      } else {
        const reportIds = [...selectedIds].slice(0, MAX_REPORTS_PER_REQUEST);
        if (reportIds.length === 0) return;
        const response = await ConcurExpenseProcessingApi.processReports(reportIds, token);
        applyResults(response);
      }
    } catch (error) {
      const monthly = request.type === 'monthly';
      showInfoModal?.(
        monthly ? 'Monthly queue failed' : 'Concur processing failed',
        <p className="mb-0">{getErrorMessage(error, monthly ? 'Could not queue monthly processing.' : 'Could not process the selected Concur report(s).')}</p>
      );
    } finally {
      setProcessing(null);
    }
  };

  const requestMonthlyQueue = (params) => setConfirmation({ type: 'monthly', params });
  const reportsProcessing = processing === 'selected' || processing === 'single';

  return (
    <div className="concur-processing-panel">
      <Tabs activeKey={mode} onSelect={(key) => setMode(key || 'selected')} className="mb-0 concur-mode-tabs manual-posting-tabs">
        <Tab eventKey="selected" title="Selected reports">
          <div className="concur-selected-workspace">
            <div className="concur-report-list-pane extraction-panel">
              <div className="d-flex flex-wrap align-items-end justify-content-between gap-3 mb-3">
                <div className="concur-company-filter">
                  {/* <Form.Label htmlFor="concur-company-filter" className="fw-bold">Company</Form.Label>
                  <Form.Select
                    id="concur-company-filter"
                    value={companyId}
                    disabled={processing !== null}
                    onChange={(event) => setCompanyId(event.target.value)}
                  >
                    <option value="">All companies</option>
                    {companies.map((company) => (
                      <option key={company.companyId} value={company.companyId}>
                        {company.companyName || `Company ${company.companyId}`}
                      </option>
                    ))}
                  </Form.Select> */}
                </div>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <Badge bg={selectedIds.size > 0 ? 'primary' : 'secondary'} className="concur-selected-count">
                    {selectedIds.size} report{selectedIds.size === 1 ? '' : 's'} selected
                  </Badge>
                  <Button
                    variant="primary"
                    size='sm'
                    disabled={selectedIds.size === 0 || processing !== null}
                    onClick={() => setConfirmation({ type: 'selected', count: selectedIds.size })}
                  >
                    {processing === 'selected' && <Spinner animation="border" size="sm" className="me-2" />}
                    {processing === 'selected' ? 'Processing reports...' : 'Process selected reports'}
                  </Button>
                </div>
              </div>

              {reportsProcessing && (
                <Alert variant="info" className="py-2 small" role="status" aria-live="polite">
                  Waiting for Concur report processing to finish. Report selection and processing actions are temporarily disabled.
                </Alert>
              )}
              {selectedIds.size >= MAX_REPORTS_PER_REQUEST && (
                <Alert variant="warning" className="py-2 small">
                  The maximum of {MAX_REPORTS_PER_REQUEST} reports is selected. Clear a selection before choosing another report.
                </Alert>
              )}
              {loadError && <Alert variant="danger" className="py-2 small">{loadError}</Alert>}

              <ServerTable
                key={`concur-reports-${companyId}`}
                columns={columns}
                fetchData={fetchReports}
                tableClass="table-hover table-sm align-middle"
                containerClass="table-responsive concur-reports-table"
                headerClass="table-light"
                enableGlobalFilter={false}
                // globalFilterPlaceholder="Search report ID, name, owner username or display name..."
                initialPageSize={50}
                pageSizeOptions={[25, 50, 100, 200]}
                refreshKey={refreshTick}
                getRowClassName={(report) => report.canProcess ? '' : 'concur-report-blocked'}
              />
            </div>

            <aside className="concur-results-pane" aria-label="Processing results panel">
              {result ? (
                <ConcurProcessingResults result={result} />
              ) : (
                <div className="concur-results-empty text-muted small">
                  Processing results will appear here after one or more reports finish processing.
                </div>
              )}
            </aside>
          </div>
        </Tab>

        <Tab eventKey="monthly" title="Monthly bulk processing">
          <MonthlyBulkPanel
            busy={processing !== null}
            onRequestQueue={requestMonthlyQueue}
            queuedJob={queuedJob}
          />
        </Tab>
      </Tabs>

      <ConfirmationModal
        show={!!confirmation}
        onHide={() => processing === null && setConfirmation(null)}
        onCancelClick={() => setConfirmation(null)}
        onAcceptClick={executeConfirmed}
        title={confirmation?.type === 'monthly' ? 'Confirm monthly Concur processing' : 'Confirm Concur report processing'}
        acceptText={confirmation?.type === 'monthly' ? 'Queue workflow' : 'Process reports'}
        acceptVariant="primary"
        acceptDisabled={processing !== null}
        cancelDisabled={processing !== null}
        closeDisabled={processing !== null}
        content={confirmation && (
          confirmation.type === 'monthly' ? (
            <p className="mb-0">
              Queue the automated credit-card workflow for {confirmation.params?.year
                ? <strong>{confirmation.params.year}-{String(confirmation.params.month).padStart(2, '0')}</strong>
                : <strong>the previous calendar month</strong>}? The job will run in the background.
            </p>
          ) : (
            <div>
              <Alert variant="warning">
                Successful processing submits and closes the report{confirmation.type === 'selected' && confirmation.count !== 1 ? 's' : ''} in Concur.
              </Alert>
              <p className="mb-0">
                {confirmation.type === 'single'
                  ? <>Process <strong>{confirmation.reportName}</strong>?</>
                  : <>Process <strong>{confirmation.count}</strong> selected report{confirmation.count === 1 ? '' : 's'}?</>}
              </p>
            </div>
          )
        )}
      />
    </div>
  );
};

export default ConcurExpenseProcessingPanel;
